// build: assembly-qc-refresh-2
import { useMemo, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Trash2, CheckCircle2, Save, Lock, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  useDeleteAssemblyDevice,
  useUpdateAssemblyDevice,
  type DeviceAssemblyRow,
} from "@/hooks/use-device-assembly";

interface Props {
  rows: DeviceAssemblyRow[];
}

const monthSortKey = (mmYy: string) => {
  const [mm, yy] = mmYy.split("-");
  return Number(`20${yy}${mm.padStart(2, "0")}`);
};

const naturalSerialCompare = (a: string, b: string) => {
  const na = parseInt(a.replace(/^SSL/i, ""), 10);
  const nb = parseInt(b.replace(/^SSL/i, ""), 10);
  if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b);
};

export const AssemblyTable = ({ rows }: Props) => {
  const months = useMemo(() => {
    const set = new Set(rows.map((r) => r.build_month));
    return Array.from(set).sort((a, b) => monthSortKey(a) - monthSortKey(b));
  }, [rows]);

  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null);

  useEffect(() => {
    if (months.length === 0) {
      setSelectedMonth(null);
      return;
    }
    if (!selectedMonth || !months.includes(selectedMonth)) {
      setSelectedMonth(months[months.length - 1]);
    }
  }, [months, selectedMonth]);

  const batches = useMemo(() => {
    if (!selectedMonth) return [];
    const set = new Set(
      rows.filter((r) => r.build_month === selectedMonth).map((r) => r.batch_id)
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [rows, selectedMonth]);

  useEffect(() => {
    if (batches.length === 0) {
      setSelectedBatch(null);
      return;
    }
    if (selectedBatch === null || !batches.includes(selectedBatch)) {
      setSelectedBatch(batches[batches.length - 1]);
    }
  }, [batches, selectedBatch]);

  const filtered = useMemo(() => {
    return rows
      .filter(
        (r) =>
          (!selectedMonth || r.build_month === selectedMonth) &&
          (selectedBatch === null || r.batch_id === selectedBatch)
      )
      .sort((a, b) => naturalSerialCompare(a.serial_number, b.serial_number));
  }, [rows, selectedMonth, selectedBatch]);

  const builtByOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => (r.built_by || "").trim()).filter(Boolean)),
      ).sort(),
    [rows],
  );
  const checkedByOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => (r.checked_by || "").trim()).filter(Boolean)),
      ).sort(),
    [rows],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assembled Devices</CardTitle>
        <div className="space-y-3 pt-2">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Build month</div>
            <div className="flex flex-wrap gap-2">
              {months.length === 0 && (
                <span className="text-sm text-muted-foreground">No records yet</span>
              )}
              {months.map((m) => (
                <Badge
                  key={m}
                  variant={m === selectedMonth ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setSelectedMonth(m)}
                >
                  {m}
                </Badge>
              ))}
            </div>
          </div>
          {batches.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Batch</div>
              <div className="flex flex-wrap gap-2">
                {batches.map((b) => (
                  <Badge
                    key={b}
                    variant={b === selectedBatch ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setSelectedBatch(b)}
                  >
                    Batch {b}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="[&_th]:px-2 [&_td]:px-2">
            <TableHeader>
              <TableRow>
                <TableHead>Serial</TableHead>
                <TableHead>Device ID</TableHead>
                <TableHead>Main PCB</TableHead>
                <TableHead>LED PCB</TableHead>
                <TableHead>Built By</TableHead>
                <TableHead className="text-center w-12" title="Night Camera (IR) at End (Yellow Dot)">Night Camera at End<br />(yellow)</TableHead>
                <TableHead className="text-center w-12" title="Day Camera (Non-IR) in Centre">Day Camera in Centre</TableHead>
                <TableHead className="text-center w-12" title="Lens Cover removal x 2">Lens Covers<br />Removed</TableHead>
                <TableHead className="text-center w-12" title="Programming complete">Prog.<br />Done</TableHead>
                <TableHead className="text-center w-12" title="Switch Button at Correct Position">Switch<br />in Place</TableHead>
                <TableHead>Checked By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center text-muted-foreground">
                    No devices in this batch.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <AssemblyRowEditor
                    key={r.id}
                    row={r}
                    builtByOptions={builtByOptions}
                    checkedByOptions={checkedByOptions}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Inline editor for a single row
// -----------------------------------------------------------------------------

interface RowEditorProps {
  row: DeviceAssemblyRow;
  builtByOptions: string[];
  checkedByOptions: string[];
}

const AssemblyRowEditor = ({ row, builtByOptions, checkedByOptions }: RowEditorProps) => {
  const locked = !!row.completed_at;
  const updateMut = useUpdateAssemblyDevice();
  const deleteMut = useDeleteAssemblyDevice();

  const parseLedPcb = (v: string | null) => {
    if (!v) return { prefix: "", suffix: "" };
    const parts = v.trim().split(/\s+/);
    return { prefix: parts[0] || "", suffix: parts[1] || "" };
  };
  const initial = parseLedPcb(row.led_pcb_serial_number);

  const [ledPrefix, setLedPrefix] = useState(initial.prefix);
  const [ledSuffix, setLedSuffix] = useState(initial.suffix);
  const [builtBy, setBuiltBy] = useState(row.built_by ?? "");
  const [checkedBy, setCheckedBy] = useState(row.checked_by ?? "");
  const [qc1, setQc1] = useState(row.qc_night_camera_end);
  const [qc2, setQc2] = useState(row.qc_day_camera_center);
  const [qc3, setQc3] = useState(row.qc_lens_covers_removed);
  const [qc4, setQc4] = useState(row.qc_programming_complete);
  const [qc5, setQc5] = useState(row.qc_switch_position);

  useEffect(() => {
    const p = parseLedPcb(row.led_pcb_serial_number);
    setLedPrefix(p.prefix);
    setLedSuffix(p.suffix);
    setBuiltBy(row.built_by ?? "");
    setCheckedBy(row.checked_by ?? "");
    setQc1(row.qc_night_camera_end);
    setQc2(row.qc_day_camera_center);
    setQc3(row.qc_lens_covers_removed);
    setQc4(row.qc_programming_complete);
    setQc5(row.qc_switch_position);
  }, [row]);

  const ledValid =
    (ledPrefix === "" && ledSuffix === "") ||
    (/^\d{6}$/.test(ledPrefix) && /^\d{4}$/.test(ledSuffix));
  const ledPresent = /^\d{6}$/.test(ledPrefix) && /^\d{4}$/.test(ledSuffix);
  const allChecks = qc1 && qc2 && qc3 && qc4 && qc5;
  const canComplete =
    !locked &&
    ledPresent &&
    allChecks &&
    builtBy.trim().length > 0 &&
    checkedBy.trim().length > 0;

  const buildPatch = () => ({
    led_pcb_serial_number: ledPresent ? `${ledPrefix} ${ledSuffix}` : null,
    built_by: builtBy.trim() || null,
    checked_by: checkedBy.trim() || null,
    qc_night_camera_end: qc1,
    qc_day_camera_center: qc2,
    qc_lens_covers_removed: qc3,
    qc_programming_complete: qc4,
    qc_switch_position: qc5,
  });

  const handleSave = async () => {
    if (!ledValid) {
      toast.error("LED PCB must be 6 digits + 4 digits");
      return;
    }
    try {
      await updateMut.mutateAsync({ id: row.id, patch: buildPatch() });
      toast.success("Saved");
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
    }
  };

  const handleComplete = async () => {
    if (!canComplete) return;
    try {
      const { data: session } = await supabase.auth.getSession();
      await updateMut.mutateAsync({
        id: row.id,
        patch: {
          ...buildPatch(),
          completed_at: new Date().toISOString(),
          completed_by: session.session?.user.id ?? null,
        },
      });
      toast.success("Marked complete");
    } catch (e: any) {
      toast.error(e?.message || "Complete failed");
    }
  };

  const handleReopen = async () => {
    try {
      await updateMut.mutateAsync({
        id: row.id,
        patch: { completed_at: null, completed_by: null },
      });
      toast.success("Reopened");
    } catch (e: any) {
      toast.error(e?.message || "Reopen failed");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMut.mutateAsync(row.id);
      toast.success("Deleted");
    } catch (e: any) {
      toast.error(e?.message || "Delete failed");
    }
  };

  return (
    <TableRow className={locked ? "bg-muted/30" : undefined}>
      <TableCell className="font-medium">{row.serial_number}</TableCell>
      <TableCell className="font-mono text-xs">{row.device_id}</TableCell>
      <TableCell className="font-mono whitespace-nowrap">{row.pcb_serial_number}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Input
            value={ledPrefix}
            onChange={(e) => setLedPrefix(e.target.value.replace(/\D/g, ""))}
            maxLength={6}
            inputMode="numeric"
            placeholder="000000"
            disabled={locked}
            className="h-8 w-20 font-mono text-xs"
          />
          <span className="text-muted-foreground">-</span>
          <Input
            value={ledSuffix}
            onChange={(e) => setLedSuffix(e.target.value.replace(/\D/g, ""))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            disabled={locked}
            className="h-8 w-16 font-mono text-xs"
          />
        </div>
      </TableCell>
      <TableCell>
        <NameCombobox
          value={builtBy}
          onChange={setBuiltBy}
          options={builtByOptions}
          disabled={locked}
          placeholder="Builder"
        />
      </TableCell>
      <TableCell className="text-center w-12">
        <Checkbox checked={qc1} disabled={locked} onCheckedChange={(v) => setQc1(!!v)} />
      </TableCell>
      <TableCell className="text-center w-12">
        <Checkbox checked={qc2} disabled={locked} onCheckedChange={(v) => setQc2(!!v)} />
      </TableCell>
      <TableCell className="text-center w-12">
        <Checkbox checked={qc3} disabled={locked} onCheckedChange={(v) => setQc3(!!v)} />
      </TableCell>
      <TableCell className="text-center w-12">
        <Checkbox checked={qc4} disabled={locked} onCheckedChange={(v) => setQc4(!!v)} />
      </TableCell>
      <TableCell className="text-center w-12">
        <Checkbox checked={qc5} disabled={locked} onCheckedChange={(v) => setQc5(!!v)} />
      </TableCell>
      <TableCell>
        <NameCombobox
          value={checkedBy}
          onChange={setCheckedBy}
          options={checkedByOptions}
          disabled={locked}
          placeholder="Checker"
        />
      </TableCell>
      <TableCell>
        {locked ? (
          <Badge variant="default" className="whitespace-nowrap">
            <Lock className="h-3 w-3 mr-1" />
            {format(new Date(row.completed_at!), "dd MMM")}
          </Badge>
        ) : (
          <Badge variant="outline">In progress</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {!locked && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSave}
                disabled={updateMut.isPending || !ledValid}
                title="Save changes"
              >
                {updateMut.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
              </Button>
              <Button
                size="sm"
                onClick={handleComplete}
                disabled={!canComplete || updateMut.isPending}
                className="bg-green-600 hover:bg-green-700 text-white"
                title="Confirm & lock"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Confirm
              </Button>
            </>
          )}
          {locked && (
            <Button size="sm" variant="outline" onClick={handleReopen} disabled={updateMut.isPending}>
              Reopen
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" title="Delete">
                <Trash2 className="h-3 w-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete assembly record?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes {row.serial_number} ({row.device_id}) from the assembly log.
                  This does not affect BuildShip.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
};

// -----------------------------------------------------------------------------
// Simple combobox: free-text input + popover of previous names
// -----------------------------------------------------------------------------

interface NameComboboxProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
}

const NameCombobox = ({ value, onChange, options, disabled, placeholder }: NameComboboxProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="h-8 w-32 text-xs"
      />
      {options.length > 0 && !disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-6 p-0" title="Pick previous">
              <ChevronDown className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="end">
            <div className="max-h-56 overflow-auto">
              {options.map((o) => (
                <button
                  key={o}
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent"
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                  }}
                >
                  {o}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};